/**
 * ResetPassword Store - State management for ResetPassword.tsx page
 *
 * Related page: frontend/src/pages/ResetPassword.tsx
 *
 * This store demonstrates the "form with validation" pattern:
 * - resetPasswordFormAtom: form data (newPassword, confirmPassword, isSubmitting)
 * - resetPasswordFormErrorsAtom: null before first submit, then contains errors
 * - Once submitted, errors update in real-time as user types
 */

import { atom } from 'jotai'
import { authClient } from '../client'
import { FormError } from 'txom-frontend-libs'

// ============================================================================
// FORM STATE
// ============================================================================

const initialResetPasswordForm = {
  newPassword: '',
  confirmPassword: '',
  isSubmitting: false,
}

type ResetPasswordForm = typeof initialResetPasswordForm

export const resetPasswordFormAtom = atom(initialResetPasswordForm)

// ============================================================================
// FORM ERRORS
// - null: form has never been submitted, don't show errors
// - object: form has been submitted, show errors and validate in real-time
// ============================================================================

type ResetPasswordFormErrors = {
  form?: string // General form-level error (e.g., "Failed to reset password")
  newPassword?: string
  confirmPassword?: string
}

export const resetPasswordFormErrorsAtom = atom<ResetPasswordFormErrors | null>(null)

// ============================================================================
// VALIDATION
// ============================================================================

function hasFieldErrors(errors: ResetPasswordFormErrors): boolean {
  const { form: _form, ...fieldErrors } = errors
  return Object.keys(fieldErrors).length > 0
}

const validateResetPasswordFormAtom = atom(null, (get, set) => {
  const form = get(resetPasswordFormAtom)
  const errors: ResetPasswordFormErrors = {}

  if (!form.newPassword) {
    errors.newPassword = 'Password is required'
  } else if (form.newPassword.length < 8) {
    errors.newPassword = 'Password must be at least 8 characters'
  }

  if (!form.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password'
  } else if (form.newPassword !== form.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match'
  }

  set(resetPasswordFormErrorsAtom, errors)
  return hasFieldErrors(errors)
})

const setResetPasswordFormErrorAtom = atom(null, (_get, set, err: unknown, fallback: string) => {
  const message = err instanceof FormError ? err.message : (err instanceof Error ? err.message || fallback : fallback)
  set(resetPasswordFormErrorsAtom, (current) => ({ ...(current ?? {}), form: message }))
})

// ============================================================================
// FORM ACTIONS
// ============================================================================

export const updateResetPasswordFormAtom = atom(null, (get, set, updates: Partial<ResetPasswordForm>) => {
  const current = get(resetPasswordFormAtom)
  set(resetPasswordFormAtom, { ...current, ...updates })

  // If form has been submitted before (errors !== null), validate in real-time
  if (get(resetPasswordFormErrorsAtom) !== null) {
    set(validateResetPasswordFormAtom)
  }
})

export const submitResetPasswordAtom = atom(null, async (get, set, token: string) => {
  const form = get(resetPasswordFormAtom)

  // Validate and enable real-time validation mode
  if (set(validateResetPasswordFormAtom)) {
    throw new FormError('Please fix the errors above')
  }

  set(resetPasswordFormAtom, { ...form, isSubmitting: true })

  try {
    const res = await authClient.resetPassword({ token, newPassword: form.newPassword })
    if (res.error) {
      throw new FormError(res.error.message || 'Failed to reset password')
    }
  } catch (err) {
    set(setResetPasswordFormErrorAtom, err, 'Failed to reset password')
    throw err
  } finally {
    set(resetPasswordFormAtom, (current) => ({ ...current, isSubmitting: false }))
  }
})

// ============================================================================
// ROUTE LOADERS
// ============================================================================

type Store = ReturnType<typeof import('jotai').createStore>

export async function loader(_store: Store) {
  return null
}
